import { OnCompletion, ProcessorDefinition } from '@kaoto/camel-catalog/types';
import { isDefined } from '@kaoto/forms';

import { getCamelRandomId } from '../../../camel-utils/camel-random-id';
import { CatalogKind } from '../../catalog-kind';
import { EntityType } from '../../entities/base-entity';
import { BaseVisualEntity, IVisualizationNode, IVisualizationNodeData, NodeInteraction } from '../base-visual-entity';
import { NodeIdentity } from '../node-identity';
import { AbstractCamelVisualEntity } from './abstract-camel-visual-entity';
import { NodeEnrichmentService } from './nodes/node-enrichment.service';
import { NodeMapperService } from './nodes/node-mapper.service';
import { CamelComponentSchemaService } from './support/camel-component-schema.service';
import { ProcessorStepsService } from './support/processor-steps.service';

export class CamelOnCompletionVisualEntity
  extends AbstractCamelVisualEntity<{ onCompletion: OnCompletion }>
  implements BaseVisualEntity
{
  id: string;
  readonly type = EntityType.OnCompletion;
  static readonly ROOT_PATH = 'onCompletion';

  constructor(public onCompletionDef: { onCompletion: OnCompletion } = { onCompletion: {} }) {
    super(onCompletionDef);
    const id = onCompletionDef.onCompletion.id ?? getCamelRandomId(CamelOnCompletionVisualEntity.ROOT_PATH);
    this.id = id;
    this.onCompletionDef.onCompletion.id = id;
  }

  static isApplicable(onCompletionDef: unknown): onCompletionDef is { onCompletion: OnCompletion } {
    if (!isDefined(onCompletionDef) || Array.isArray(onCompletionDef) || typeof onCompletionDef !== 'object') {
      return false;
    }

    const objectKeys = Object.keys(onCompletionDef!);

    return (
      objectKeys.length === 1 && this.ROOT_PATH in onCompletionDef! && typeof onCompletionDef.onCompletion === 'object'
    );
  }

  getRootPath(): string {
    return CamelOnCompletionVisualEntity.ROOT_PATH;
  }

  getId(): string {
    return this.id;
  }

  setId(id: string): void {
    this.id = id;
    this.onCompletionDef.onCompletion.id = id;
  }

  getNodeInteraction(data: IVisualizationNodeData): NodeInteraction {
    const stepsProperties = ProcessorStepsService.getProcessorStepsProperties(
      data.primaryNodeId?.name as keyof ProcessorDefinition,
    );
    const canHavePreviousStep = ProcessorStepsService.canHavePreviousStep(
      data.primaryNodeId?.name as keyof ProcessorDefinition,
    );
    const canHaveChildren = stepsProperties.some((property) => property.type === 'branch');
    const canHaveSpecialChildren = Object.keys(stepsProperties).length > 1;
    const canReplaceStep = data.path !== CamelOnCompletionVisualEntity.ROOT_PATH;
    const canRemoveStep = data.path !== CamelOnCompletionVisualEntity.ROOT_PATH;
    const canBeDisabled = CamelComponentSchemaService.canBeDisabled(
      data.primaryNodeId?.name as keyof ProcessorDefinition,
    );

    return {
      canHavePreviousStep,
      canHaveNextStep: canHavePreviousStep,
      canHaveChildren,
      canHaveSpecialChildren,
      canReplaceStep,
      canRemoveStep,
      canRemoveFlow: data.path === CamelOnCompletionVisualEntity.ROOT_PATH,
      canBeDisabled,
    };
  }

  async toVizNode(): Promise<IVisualizationNode<IVisualizationNodeData>> {
    const onCompletionGroupNode = await NodeMapperService.getVizNode(
      CamelOnCompletionVisualEntity.ROOT_PATH,
      {
        primaryNodeId: {
          name: CamelOnCompletionVisualEntity.ROOT_PATH as keyof ProcessorDefinition,
          catalogKind: CatalogKind.Entity,
        } satisfies NodeIdentity,
      },
      this.onCompletionDef,
    );
    onCompletionGroupNode.data.entity = this;
    onCompletionGroupNode.data.isGroup = true;
    onCompletionGroupNode.data.catalogKind = CatalogKind.Entity;
    onCompletionGroupNode.data.name = this.type;
    onCompletionGroupNode.data.primaryNodeId = { name: this.type, catalogKind: CatalogKind.Entity };

    await NodeEnrichmentService.enrichVisualizationTree(onCompletionGroupNode);

    return onCompletionGroupNode;
  }

  toJSON(): { onCompletion: OnCompletion } {
    return { onCompletion: this.onCompletionDef.onCompletion };
  }

  protected getRootUri(): string | undefined {
    return undefined;
  }
}
